import { useCallback, useState } from 'react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';

import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ITableHeader, Table } from '../../../components/table/Table';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { Input } from '../../../components/input/Input';
import { PageHeading } from 'components/PageHeading';
import { ListPage } from 'components/page-templates/ListPage';
import { useDebouncedSearchParams } from 'src/hooks/useDebouncedSearchParams';
import { FileUploaderButton } from '../../document/components/FileUploaderButton';
import { useTranslation } from '../../../contexts/translation-context';
import { getCarrierPortalSelfBills, CarrierPortalSelfBill } from '../models';
import { useCarrierPortalSupplier } from '../CarrierPortalCarrierContext';
import { SelfBillingMode } from 'src/types/enums';

const CarrierPortalSelfBillingsPage = () => {
  const { i18n } = useTranslation();
  const { carrierId, supplier } = useCarrierPortalSupplier();
  // Ride-overview wording unless the carrier is self-billed now (also for a carrier set to None).
  const isRideOverview = supplier.selfBillingMode !== SelfBillingMode.SelfBilling;
  const [searchParams, setSearchParams] = useDebouncedSearchParams();
  const [searchValue, _setSearchValue] = useState(() => searchParams['search'] ?? '');

  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getCarrierPortalSelfBills(carrierId, variables.cursor, variables.take, {
        search: searchValue,
      });
    },
    [carrierId, searchValue],
  );

  const page = usePagination<CarrierPortalSelfBill>({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const setSearchValue = useCallback(
    (newValue: string) => {
      setSearchParams({ search: newValue });
      _setSearchValue(newValue);
    },
    [_setSearchValue],
  );

  const SELF_BILL_HEADERS: ITableHeader[] = [
    {
      id: 'invoiceNumber',
      name: i18n('carrierPortal.selfBilling.number'),
    },
    {
      id: 'kind',
      name: i18n('carrierPortal.selfBilling.type'),
    },
    {
      id: 'entryNumber',
      name: i18n('carrierPortal.selfBilling.id'),
    },
    {
      id: 'createdAt',
      name: i18n('carrierPortal.selfBilling.date'),
    },
    {
      id: 'download',
      name: i18n('carrierPortal.selfBilling.download'),
    },
  ];

  const title = i18n(
    isRideOverview ? 'carrierPortal.selfBilling.rideOverview' : 'carrierPortal.selfBilling.selfBilling',
  );

  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={<PageHeading noMarginBottom leftSide={<Breadcrumb currentItem={title} />} />}
        filters={
          <Input
            type="text"
            placeholder={i18n(
              isRideOverview
                ? 'carrierPortal.selfBilling.searchRideOverviewPlaceholder'
                : 'carrierPortal.selfBilling.searchPlaceholder',
            )}
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={SELF_BILL_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(selfBill) => {
            const kindLabel = i18n(
              selfBill.kind === 'RideOverview'
                ? 'carrierPortal.selfBilling.rideOverview'
                : 'carrierPortal.selfBilling.selfBilling',
            );
            return [
              selfBill.invoiceNumber ?? '-',
              kindLabel,
              selfBill.entryNumber,
              formatDate(selfBill.createdAt),
              <FileUploaderButton
                buttonText={i18n(
                  selfBill.kind === 'RideOverview'
                    ? 'carrierPortal.selfBilling.download'
                    : 'carrierPortal.selfBilling.invoice',
                )}
                title={selfBill.kind === 'RideOverview' ? kindLabel : i18n('carrierPortal.selfBilling.title')}
                file={selfBill.document}
                disableUpload
              />,
            ];
          }}
        />
      </ListPage>
    </>
  );
};

export default CarrierPortalSelfBillingsPage;
